import { z } from "zod";

/** Schemes offered ready-made in the list. The operator can also type any
 *  scheme by hand — it only has to satisfy the `normalizeScheme` rules. */
export const FORMATION_PRESETS = [
  "4-3-3",
  "4-4-2",
  "4-2-3-1",
  "4-1-4-1",
  "4-4-1-1",
  "4-3-2-1",
  "4-1-2-1-2",
  "4-5-1",
  "3-5-2",
  "3-4-3",
  "3-4-2-1",
  "5-3-2",
  "5-4-1",
] as const;

/** Outfield players (without the goalkeeper). */
const OUTFIELD = 10;

/** "4231", "4 2 3 1", "4.2.3.1" → "4-2-3-1". 2–5 lines, 1–5 players per
 *  line (the grid has 9 columns, 5 rows), sum 10. Otherwise null. */
export function normalizeScheme(raw: string): string | null {
  const compact = raw.trim();
  const parts = /^\d+$/.test(compact) ? compact.split("") : compact.split(/[\s.\-–]+/);
  if (parts.length < 2 || parts.length > 5) return null;
  const lines = parts.map((p) => (/^[1-5]$/.test(p) ? Number(p) : Number.NaN));
  if (lines.some(Number.isNaN)) return null;
  if (lines.reduce((a, b) => a + b, 0) !== OUTFIELD) return null;
  return lines.join("-");
}

/** Scheme lines from defence to attack:"4-2-3-1" → [4, 2, 3, 1]. */
export function schemeLines(scheme: string): number[] {
  return scheme.split("-").map(Number);
}

export const formationSchemeSchema = z
  .string()
  .refine((s) => normalizeScheme(s) === s, { message: "invalid_scheme" });
export type FormationScheme = z.infer<typeof formationSchemeSchema>;

export const SLOT_COUNT = 11;

// One slot's contents in the live state. Carries enough player info that
// the overlay can render without an extra DB lookup — the dashboard fills
// these from the match roster when assigning.
export const lineupSlotSchema = z.object({
  playerId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  number: z.number().int().nonnegative().nullable(),
});
export type LineupSlot = z.infer<typeof lineupSlotSchema>;

export const EMPTY_SLOT: LineupSlot = {
  playerId: null,
  firstName: "",
  lastName: "",
  number: null,
};

// Per-side formation memory — the Control Room quick-edit and TabLineup keep
// each team's scheme here; `scheme` below stays what the grid graphic renders.
export const lineupSchemesSchema = z.object({
  home: formationSchemeSchema.default("4-3-3"),
  away: formationSchemeSchema.default("4-3-3"),
});
export type LineupSchemes = z.infer<typeof lineupSchemesSchema>;

export const lineupStateSchema = z.object({
  visible: z.boolean().default(false),
  side: z.enum(["home", "away"]).nullable().default(null),
  scheme: formationSchemeSchema.default("4-3-3"),
  schemes: lineupSchemesSchema.default({ home: "4-3-3", away: "4-3-3" }),
  slots: z.array(lineupSlotSchema).length(SLOT_COUNT),
  shownAt: z.number().int().nonnegative().nullable().default(null),
});
export type LineupState = z.infer<typeof lineupStateSchema>;

export function emptyLineup(): LineupState {
  return {
    visible: false,
    side: null,
    scheme: "4-3-3",
    schemes: { home: "4-3-3", away: "4-3-3" },
    slots: Array.from({ length: SLOT_COUNT }, () => ({ ...EMPTY_SLOT })),
    shownAt: null,
  };
}
